import { useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { Controller, useForm } from "react-hook-form";
import { Link } from "react-router-dom";
import { CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { z } from "zod";
import { PasswordRequirements } from "@/components/auth/PasswordRequirements";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/lib/auth";
import { passwordSchema } from "@/lib/passwordValidation";
import { DEFAULT_CLOSED_MESSAGE } from "./topShopRegistration";

const TOPSHOP_HOSTS = ["topshopchallenge.com", "www.topshopchallenge.com"];

const schema = z.object({
  fullName: z.string().trim().min(2, "Name must be at least 2 characters").max(120, "Full name must be 120 characters or fewer"),
  email: z.string().trim().email("Invalid email address").max(255, "Email must be 255 characters or fewer"),
  password: passwordSchema,
  privacyPolicy: z.literal(true, { errorMap: () => ({ message: "You must agree to the Privacy Policy" }) }),
});
type Values = z.infer<typeof schema>;

function FieldError({ message }: { message?: string }) {
  if (!message) return null;
  return <p role="alert" className="text-sm font-medium text-destructive">{message}</p>;
}

export function TopShopClosedMessage({ message }: { message?: string | null }) {
  return <p className="whitespace-pre-line leading-7 text-muted-foreground">{message?.trim() ? message : DEFAULT_CLOSED_MESSAGE}</p>;
}

export default function TopShopRegistrationClosed({ message }: { message?: string | null }) {
  const { user, signUp } = useAuth();
  const isTopShopHost = TOPSHOP_HOSTS.includes(window.location.hostname);
  const showSignup = isTopShopHost || !user;
  const [done, setDone] = useState<string | null>(null);
  const form = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: { fullName: "", email: "", password: "", privacyPolicy: false as true },
  });
  const errors = form.formState.errors;

  const onSubmit = async (values: Values) => {
    const { error } = await signUp(values.email, values.password, values.fullName, "entrepreneur", {
      extraMetadata: { source: isTopShopHost ? "topshopchallenge.com" : "eabconnect.com", signup_context: "top-shop-closed" },
      emailRedirectTo: "https://eabconnect.com/dashboard",
    });
    if (error) { toast.error(error.message); return; }
    setDone(values.email);
  };

  return (
    <div className="rounded-md border bg-background p-5 shadow-sm sm:p-8 lg:p-10">
      <h2 className="text-3xl font-bold tracking-normal sm:text-4xl">Registration is closed</h2>
      <div className="mt-4"><TopShopClosedMessage message={message} /></div>

      {!showSignup ? (
        <Button asChild className="mt-6"><Link to="/dashboard">Go to your dashboard</Link></Button>
      ) : done ? (
        <div className="mt-8 rounded-md border border-success/30 bg-success/5 p-6">
          <CheckCircle2 className="h-10 w-10 text-success" aria-hidden="true" />
          <p className="mt-4 leading-7 text-muted-foreground">
            Welcome to EAB Connect. We sent a confirmation link to <strong className="text-foreground">{done}</strong>. Click it to confirm your address, then sign in at eabconnect.com with the email and password you just created. Then complete your profile so our team can approve it and other members can find you.
          </p>
        </div>
      ) : (
        <form onSubmit={form.handleSubmit(onSubmit)} className="mt-8 space-y-5" noValidate>
          <h3 className="text-xl font-bold">Join EAB Connect</h3>
          <div className="grid gap-5 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="closed-full-name">Full name</Label>
              <Input id="closed-full-name" autoComplete="name" {...form.register("fullName")} aria-invalid={Boolean(errors.fullName)} />
              <FieldError message={errors.fullName?.message} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="closed-email">Email</Label>
              <Input id="closed-email" type="email" autoComplete="email" {...form.register("email")} aria-invalid={Boolean(errors.email)} />
              <FieldError message={errors.email?.message} />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="closed-password">Password</Label>
            <Input id="closed-password" type="password" autoComplete="new-password" {...form.register("password")} aria-invalid={Boolean(errors.password)} />
            <PasswordRequirements password={form.watch("password") ?? ""} />
            <FieldError message={errors.password?.message} />
          </div>
          <div className="flex items-start gap-3">
            <Controller name="privacyPolicy" control={form.control} render={({ field }) => <Checkbox id="closed-privacy" checked={field.value} onCheckedChange={(v) => field.onChange(v === true)} />} />
            <div>
              <Label htmlFor="closed-privacy" className="cursor-pointer font-normal">I agree to the <Link to="/privacy-policy" target="_blank" className="font-medium text-primary hover:underline">Privacy Policy</Link></Label>
              <FieldError message={errors.privacyPolicy?.message} />
            </div>
          </div>
          <Button type="submit" size="lg" disabled={form.formState.isSubmitting} className="w-full bg-warning text-warning-foreground hover:bg-warning/90 sm:w-auto">
            {form.formState.isSubmitting && <Loader2 className="animate-spin" aria-hidden="true" />}
            Join EAB Connect
          </Button>
          <p className="text-muted-foreground">Already on EAB Connect? <a href={isTopShopHost ? "https://eabconnect.com/auth" : "/auth"} className="font-semibold text-primary hover:underline">Sign in</a></p>
        </form>
      )}
    </div>
  );
}
